import moment from "moment";
import { AccordanceState } from "../../../../models/AccordanceState";
import { replaceMomentFormat } from "../../../../services/util";

const EDIT_ACTION_INDEX = 1;

const ACCORDANCE_TRANSLATION_FIELD_KEYS = {
  "AccordancesType": "type_of_accordance",
  "CriticalIssue": "critical_issue",
  "RootCauseCategory": "new_root_cause_of_critical_issue",
  "RootCause": "new_sub_categories",
  "Description": "accordance_description",
  "DescriptionCriticalIssues": "description_of_the_critical_issue",
  "SubstantialFundamentalChange": "substantial_fundamental_changes",
  "UrlPMB": "pmb",
  "DateCreated": "new_portfolio_review_date_two",
  "ExpireDate": "due_date",
  "LinkedProduct": "new_linked_product",
  "Responsible": "new_bid_responsible"
}

export enum EventAccordance {
  AccordancesType = "AccordancesType",
  ApprovedByUser = "ApprovedByUser",
  PendingApprovalByUser = "PendingApprovalByUser",
  Edited = "Edited",
  RejectedByUser = "RejectedByUser",
  Current = "Current",
  Completed = "DateCompleted",
  Canceled = "DateCanceled",
}

interface LogEvent {
  action: number;
  subsection: string;
  data?: string;
  date: string | Date;
  section: string;
  [key: string]: any;
}

export const getDateFormattedForEvent = (event, accordance) => {
  if (event.subsection === EventAccordance.AccordancesType) return accordance.creationDate?.dateFormatedDetails;
  if (event.subsection === EventAccordance.Completed && event.date === '') return '';

  return typeof event?.date === 'string' && /\d{2}\/\w{3}\/\d{4}/.test(event.date)
    ? event.date.toUpperCase()
    : replaceMomentFormat(moment(event?.date).format('DD/MMM/YYYY')) || '-';
}

export const getEventsInAccordance = (log: LogEvent[], accordance: any, translate: (subsection: string) => string) => {
  if (!log) return null;
  if (log.length === 0) return getEventsFromAccordanceWithoutLog(accordance);

  const groupedLog = groupEditedActions(log, translate);

  let events: LogEvent[] = groupedLog.filter((event: LogEvent) => {
    if (event.section !== 'accordances') return false;
    return Object.values(EventAccordance).includes(event.subsection as EventAccordance);
  });

  return filterAndPrioritizePendingEvents(events, accordance);
}

function getEventsFromAccordanceWithoutLog(accordance: any) {
  if (!accordance) return null;

  const events: any[] = [];

  if (accordance.accordanceAgreementsType) {
    events.push({
      subsection: EventAccordance.AccordancesType,
      data: accordance.accordanceAgreementsType.name,
      date: accordance.creationDate?.dateFormatedDetails,
      section: 'accordances',
    });
  }

  if (accordance.rejectionDetail) {
    events.push({
      subsection: EventAccordance.RejectedByUser,
      data: accordance.rejectionDetail?.rejectedBy
        ? `${accordance.rejectionDetail.rejectedBy.name} ${accordance.rejectionDetail.rejectedBy.lastName}` : '',
      date: accordance.rejectionDetail?.dateCreated,
      section: 'accordances',
    });
  }

  switch (accordance.status) {
    case AccordanceState.PENDING:
      events.push({
        subsection: EventAccordance.PendingApprovalByUser,
        data: accordance.personToApprove,
        date: accordance.creationDate?.dateFormated,
        section: 'accordances',
      });
      break;
    case AccordanceState.VALID:
      events.push({
        subsection: EventAccordance.Current,
        data: '',
        date: new Date().toISOString(),
        section: 'accordances',
      });
      break;
    case AccordanceState.FULLFILLED:
      events.push({
        subsection: EventAccordance.Current,
        data: '',
        date: new Date().toISOString(),
        section: 'accordances',
      });
      events.push({
        subsection: EventAccordance.Completed,
        data: '',
        date: '',
        section: 'accordances',
      });
      break;
  }

  return events.reverse();
};

function groupEditedActions(log: LogEvent[], translate: (subsection: string) => string): LogEvent[] {
  if (!Array.isArray(log)) return [];
  const editedLogEvents: LogEvent[] = [];

  let index = 0;

  while (index < log.length) {
    const currentEventLog = log[index];
    if (currentEventLog.action === EDIT_ACTION_INDEX) {
      const editSequence: LogEvent[] = [];

      let seqIndex = index;
      while (seqIndex < log.length && log[seqIndex].action === EDIT_ACTION_INDEX) {
        editSequence.push(log[seqIndex]);
        seqIndex++;
      }

      if (editSequence.length > 0) {
        const lastEdit = editSequence[editSequence.length - 1];
        const validSubsections = editSequence
          .map(event => event.subsection)
          .filter(eventSubsection => 
            !eventSubsection.endsWith('Before') 
            && Object.keys(ACCORDANCE_TRANSLATION_FIELD_KEYS).includes(eventSubsection));

        const uniqueSubsections = Array.from(new Set(validSubsections))
          .map(subsection => {
            return translate(ACCORDANCE_TRANSLATION_FIELD_KEYS[subsection] || subsection);
          });

        editedLogEvents.push({
          ...lastEdit,
          subsection: EventAccordance.Edited,
          data: uniqueSubsections.join(', '),
          date: lastEdit.date,
        });
      }

      index = seqIndex;
    } else {
      editedLogEvents.push(currentEventLog);
      index++;
    }
  }

  return editedLogEvents;
};

function filterAndPrioritizePendingEvents(events: LogEvent[], accordance: any) {
  const lastPendingEvent = events.find(e => e.subsection === EventAccordance.PendingApprovalByUser);
  const eventsWithoutPending = events.filter(e => e.subsection !== EventAccordance.PendingApprovalByUser);

  if (accordance.status !== AccordanceState.PENDING) return eventsWithoutPending;

  eventsWithoutPending.unshift({
    action: 0,
    subsection: EventAccordance.PendingApprovalByUser,
    data: accordance.personToApprove,
    date: lastPendingEvent ? lastPendingEvent.date : accordance.creationDate?.dateFormated,
    section: 'accordances',
  });

  return eventsWithoutPending;
}
